import React, { useEffect, useState } from "react";
import { Grid, Modal, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Credit } from "./types";
import PaymentRowsEditor, {
  PaymentRow,
  createPaymentRow,
} from "./PaymentRowsEditor";

const { Text } = Typography;
const { useBreakpoint } = Grid;

export type ManualPaymentRowInput = {
  amount: number;
  paymentDate: string;
  note?: string;
};

type Props = {
  open: boolean;
  credit?: Credit | null;
  saving?: boolean;
  onCancel: () => void;
  onSave: (rows: ManualPaymentRowInput[]) => void;
};

const money = (val: number) =>
  (val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ManualPaymentsModal: React.FC<Props> = ({
  open,
  credit,
  saving,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [rows, setRows] = useState<PaymentRow[]>([]);

  useEffect(() => {
    if (open) {
      setRows([
        createPaymentRow({
          amount: credit?.monthlyAmount || null,
          date: dayjs().format("YYYY-MM-DD"),
        }),
      ]);
    }
  }, [open, credit?.monthlyAmount]);

  const totalAmount = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const remaining = credit?.remainingAmount || 0;
  const exceedsRemaining = remaining > 0 && totalAmount > remaining;
  const hasValidRow = rows.some((r) => (Number(r.amount) || 0) > 0);

  const handleOk = () => {
    const payload: ManualPaymentRowInput[] = rows
      .filter((r) => (Number(r.amount) || 0) > 0)
      .map((r) => ({
        amount: Number(r.amount),
        paymentDate: dayjs(r.date).toISOString(),
        note: r.note || undefined,
      }));
    if (!payload.length) return;
    onSave(payload);
  };

  return (
    <Modal
      open={open}
      title={t("Add manual payments")}
      width={isMobile ? "100%" : 700}
      onCancel={onCancel}
      onOk={handleOk}
      okButtonProps={{ loading: saving, disabled: !hasValidRow }}
      okText={t("Save")}
      className="standardHeader standardFooter"
      destroyOnClose
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {t(
          "Enter the first installment, then duplicate it to quickly add next month's payment. Amounts are automatically capped to the remaining credit balance.",
        )}
      </Text>

      <PaymentRowsEditor rows={rows} onChange={setRows} isMobile={isMobile} />

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Text>
          {t("Total to add")}: <strong>{money(totalAmount)}</strong>
        </Text>
        {credit && (
          <Text type={exceedsRemaining ? "warning" : "secondary"}>
            {t("Remaining balance")}: {money(remaining)}
          </Text>
        )}
      </div>
      {exceedsRemaining && (
        <Text type="warning" style={{ display: "block", marginTop: 4 }}>
          {t(
            "Total exceeds remaining balance - extra amounts will be automatically capped.",
          )}
        </Text>
      )}
    </Modal>
  );
};

export default ManualPaymentsModal;
